import { Card, Divider, Flex, Form, Typography } from "antd";
import { FORM_VALIDATION_DEBOUNCE } from "constants/form";
import {
  BID_REQUEST_STATUS_ENUM,
  BidRequestStatus,
  COMPANY_ROLE_ENUM,
  FeeDetailDto,
  MODE_TYPE,
  NOTIFICATION_TYPE_ENUM,
} from "interfaces";
import { observer } from "mobx-react-lite";
import { useNotificationContext } from "providers";
import { useEffect, useState } from "react";
import { editCompanyFeeForBid } from "services";
import { useStores } from "stores";
import { CustomButton, NumberInput } from "ui";
import { formatWithDecimals, isStatusIncluded, parseErrorMessage, toFraction } from "utils";
import {
  _discountDoesNotExceedFee,
  _greaterOrEqual,
  _isNumber,
  _isRequired,
  _lessOrEqual,
  _maxDecimals,
} from "utils/form/simple-validation";
import { ItemInfo } from "components";
import { buildFeeRows } from "../BidInfoBlocks/FeeTooltip/buildFeeRows";

interface FieldType {
  customerFee?: number | string;
  lpFee?: number | string;
}

const MAX_DECIMALS = 2;
const MIN_DISPLAY_PERCENT = 0.5 / 10 ** MAX_DECIMALS;

const formatPercent = (value: number) => {
  const percent = value * 100;
  const normalizedPercent = Math.abs(percent) < MIN_DISPLAY_PERCENT ? 0 : percent;

  return `${formatWithDecimals(normalizedPercent, MAX_DECIMALS)} %`;
};

const AMOUNT_DISCOUNT_VISIBLE_STATUSES: BidRequestStatus[] = [
  BID_REQUEST_STATUS_ENUM.FUNDING,
  BID_REQUEST_STATUS_ENUM.FUNDED,
  BID_REQUEST_STATUS_ENUM.EXECUTION,
  BID_REQUEST_STATUS_ENUM.COMPLETED,
  BID_REQUEST_STATUS_ENUM.CANCELED_REFUNDING,
  BID_REQUEST_STATUS_ENUM.CANCELED_REFUNDED,
  BID_REQUEST_STATUS_ENUM.CANCELED,
];

export const EditCustomFeeModal = observer(({ mode }: { mode: MODE_TYPE }) => {
  const { actionModals, bidCard } = useStores();
  const { openNotification } = useNotificationContext();

  const [loading, setLoading] = useState(false);

  const key = mode === MODE_TYPE.CUSTOMER ? "customerFee" : "lpFee";
  const currentFee = bidCard.bid[key] || 0;
  const role = mode === MODE_TYPE.CUSTOMER ? COMPANY_ROLE_ENUM.CUSTOMER : COMPANY_ROLE_ENUM.LIQUIDITY_PROVIDER;
  const currentCustomFee = bidCard.bid.feeDetails?.find((item) => item.role === role)?.customFee;
  const feeDetails = bidCard.bid.feeDetails?.find((item) => item.role === role) as FeeDetailDto | undefined;

  const [form] = Form.useForm<FieldType>();
  const feeAvailableForDiscount = currentFee - Number(currentCustomFee || 0);
  const feeValue = Form.useWatch(key, form);
  const isFeeChanged = form.isFieldTouched(key);
  const normalizedFeeValue = Number(String(feeValue ?? 0).replace(",", "."));
  const customFee = Number.isNaN(normalizedFeeValue) ? 0 : normalizedFeeValue / 100;
  const totalFee =
    isFeeChanged && !Number.isNaN(normalizedFeeValue) ? feeAvailableForDiscount + normalizedFeeValue / 100 : currentFee;
  const amountRub = bidCard.bid.amountRub;
  const discountThreshold = feeDetails?.discountThreshold;
  const hasAmountRub = amountRub !== undefined && amountRub !== null;
  const hasDiscountThreshold = discountThreshold !== undefined && discountThreshold !== null;
  const showAmountDiscount =
    isStatusIncluded(bidCard.bid.status, AMOUNT_DISCOUNT_VISIBLE_STATUSES) &&
    hasAmountRub &&
    hasDiscountThreshold &&
    amountRub >= discountThreshold;
  const feeRows = feeDetails
    ? buildFeeRows({
        feeDetail: {
          ...feeDetails,
          customFee,
        },
        amountRub: undefined,
        showRub: false,
        showAmountDiscount,
        totalFee,
        totalFeeRub: undefined,
      })
    : [];
  const visibleFeeRows = feeRows.flatMap((row) => {
    if (row.key === "customFeePositive" || row.key === "customFeeNegative") {
      return [];
    }

    if (row.key === "total") {
      return [
        {
          key: "customFee",
          label: "Дополнительная комиссия",
          percent: customFee,
        },
        row,
      ];
    }

    return [row];
  });

  const editFeeHandler = async () => {
    setLoading(true);
    const raw = form.getFieldValue(key);
    const formData = {
      [key]: toFraction(raw),
    };

    await editCompanyFeeForBid({ id: bidCard.bid.id }, formData as any)
      .then(() => {
        setTimeout(() => bidCard.updateCardById(bidCard.bid.id), 1500);
        handleCancelEditFee();
        openNotification({
          message: "Данные успешно обновлены",
          type: NOTIFICATION_TYPE_ENUM.SUCCESS,
        });
      })
      .catch((e) => {
        openNotification({
          type: NOTIFICATION_TYPE_ENUM.ERROR,
          description: parseErrorMessage(e),
        });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleCancelEditFee = () => {
    actionModals.closeAll();
  };

  useEffect(() => {
    form.setFieldsValue({
      [key]:
        currentCustomFee === undefined || currentCustomFee === null
          ? ""
          : formatWithDecimals(currentCustomFee * 100, MAX_DECIMALS),
    });
  }, [currentCustomFee, form, key]);

  return (
    <Flex vertical gap="large">
      <Flex vertical>
        <Divider className="margin-top-none" />
        <Flex vertical className="margin-bottom-none">
          {visibleFeeRows.map((row) => (
            <ItemInfo
              key={row.key}
              title={row.isTotal ? `${row.label}:` : row.label}
              text={formatPercent(row.percent)}
              vertical={false}
            />
          ))}
        </Flex>

        <Divider className="margin-top-none margin-bottom-none" />
      </Flex>
      <Flex>
        <Card className="bg-basic-200 full-width" style={{ padding: 0 }}>
          <Flex vertical>
            <Typography.Text className={`margin-bottom-xs`} strong>
              Как ввести значение:
            </Typography.Text>
            <ul style={{ marginTop: 0, marginLeft: -16, marginBottom: 0 }}>
              <li className="text-description ">
                Для добавления комиссии введите положительное число <br /> (например: 2 означает +2%)
              </li>
              <li className="text-description ">
                Для применения скидки введите отрицательное число <br /> (например: -1.5 означает -1.5%)
              </li>
              <li className="text-description ">Дополнительная комиссия будет добавлена к основной комиссии</li>
            </ul>
          </Flex>
        </Card>
      </Flex>
      <Divider className="margin-top-none margin-bottom-none" />
      <Form form={form} name="editFee" onFinish={() => editFeeHandler()}>
        <Flex gap="small">
          <Flex vertical style={{ flex: 1 }}>
            <Form.Item
              name={key}
              rules={[
                _isRequired("Поле обязательно для ввода"),
                _isNumber("Введите корректное число"),
                _greaterOrEqual(-100),
                _lessOrEqual(100),
                _maxDecimals(MAX_DECIMALS),
                _discountDoesNotExceedFee(feeAvailableForDiscount * 100),
              ]}
              validateDebounce={FORM_VALIDATION_DEBOUNCE}
            >
              <NumberInput
                placeholder={`Дополнительная комиссия ${mode === MODE_TYPE.CUSTOMER ? "УВЭД" : "ПЛ"}, %`}
                disabled={loading}
                allowNegative
              />
            </Form.Item>
          </Flex>
        </Flex>

        <Form.Item>
          <Flex gap="large">
            <Form.Item noStyle shouldUpdate>
              {() => (
                <CustomButton
                  htmlType="submit"
                  disabled={
                    loading || !String(form.getFieldValue(key) ?? "").trim() || form.getFieldError(key).length > 0
                  }
                  loading={loading}
                  className="full-width"
                >
                  Сохранить
                </CustomButton>
              )}
            </Form.Item>
            <CustomButton
              htmlType="button"
              disabled={loading}
              loading={loading}
              type="default"
              onClick={handleCancelEditFee}
              className="full-width"
            >
              Отменить
            </CustomButton>
          </Flex>
        </Form.Item>
      </Form>
    </Flex>
  );
});
